import { ref } from 'vue';
import { defineStore } from 'pinia';
import { getImageUrl, getAltTextsTranslations as apiGetAltTextsTranslations, getAltText } from './api';

export type State = {
  images: Map<string, { url: string; altText?: { [lang: string]: string } }>;
};

const useImages = defineStore(
  'images',
  () => {
    const state = ref<State>({
      images: new Map(),
    });

    const getImageUrlByImageId = async (imageId: string): Promise<string | undefined> => {
      const existingImage = state.value.images.get(imageId);

      if (existingImage && existingImage.url) {
        return existingImage.url;
      }

      const url = await getImageUrl(imageId);
      state.value.images.set(imageId, { ...existingImage, url });

      return url;
    };

    // image alt text in one language, saved directly in content registry
    const getAltTextByImageId = async (imageId: string, lang: string): Promise<string> => {
      if (!state.value.images.has(imageId)) {
        await getImageUrlByImageId(imageId);
      }
      const image = state.value.images.get(imageId);
      if (!image?.altText || !image.altText[lang]) {
        const altText = await getAltText(imageId, lang);
        // @ts-ignore
        state.value.images.set(imageId, { ...image, altText: { [lang]: altText } });
      }
      const altText = state.value.images.get(imageId)?.altText?.[lang];
      return altText || '';
    };

    // image alt text in multiple languages, translations are in babelfish
    const getAltTextsTranslations = async (
      imageIds: string[],
      lang: string,
    ): Promise<{ [imageId: string]: string }> => {
      const notLoadedImages = imageIds.filter(
        (id) => !state.value.images.has(id) || !state.value.images.get(id)?.altText,
      );
      if (notLoadedImages.length > 0) {
        const texts = await apiGetAltTextsTranslations(notLoadedImages);
        Object.entries(texts).forEach(([imageId, translations]) => {
          const existingImage = state.value.images.get(imageId);
          if (existingImage) {
            state.value.images.set(imageId, {
              ...existingImage,
              altText: { ...existingImage.altText, ...translations },
            });
          } else {
            state.value.images.set(imageId, { url: '', altText: translations });
          }
        });
      }

      return imageIds.reduce((acc: { [imageId: string]: string }, imageId) => {
        const image = state.value.images.get(imageId);
        acc[imageId] = image?.altText?.[lang] || '';
        return acc;
      }, {});
    };

    return {
      getImageUrlByImageId,
      getAltTextByImageId,
      getAltTextsTranslations,
    };
  },
  {},
);

export default useImages;
